import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import styles from 'Components/PdfFormTemplates/pdfFormTemplates.module.css';
import {
  listPdfFormTemplates,
  createPdfFormSubmission,
  sharePdfFormSubmission,
  fetchProjectClaims,
} from './actions';
import { pdfFormTemplatesSelector } from './selectors';

interface Props {
  isOpen: boolean;
  projectId: number;
  onClose: () => void;
  onSubmissionCreated?: () => void;
}

function SendPdfFormFromProjectModal({ isOpen, projectId, onClose, onSubmissionCreated }: Props) {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const templates = useSelector(pdfFormTemplatesSelector, areEqual);
  const templateList = templates?.data || [];
  const activeTemplates = templateList.filter((t: any) => t.status === 'active');

  const [step, setStep] = useState<'pick' | 'form'>('pick');
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateSearch, setTemplateSearch] = useState('');

  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [sendMethod, setSendMethod] = useState<'email_and_sms' | 'email' | 'sms'>('email_and_sms');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Track created submission so retries only re-share instead of creating duplicates
  const [createdSubmission, setCreatedSubmission] = useState<any>(null);

  // Guard for async claim prefill so stale responses don't update a closed/reset modal
  const claimRequestIdRef = useRef(0);

  const formatPhone = useCallback((value: string) => {
    let digits = value.replace(/\D/g, '');
    let prefix = '';
    // Preserve country code '1' prefix
    if (digits.length === 11 && digits.startsWith('1')) {
      prefix = '+1 ';
      digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return `${prefix}${digits}`;
    if (digits.length <= 6) return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }, []);

  const rawPhone = clientPhone.replace(/\D/g, '');

  const filteredTemplates = activeTemplates.filter((t: any) => {
    if (!templateSearch.trim()) return true;
    return t.name.toLowerCase().includes(templateSearch.toLowerCase());
  });

  // Fetch templates when modal opens
  useEffect(() => {
    if (isOpen && companyId) {
      dispatch(listPdfFormTemplates(companyId) as any);
    }
  }, [isOpen, companyId]);

  // Prefill client info from project claims when template is selected
  const onTemplateSelect = useCallback(
    async (template: any) => {
      setSelectedTemplate(template);
      setStep('form');

      const requestId = ++claimRequestIdRef.current;
      const claims = await dispatch(fetchProjectClaims(projectId) as any);

      // Only apply if this is still the active request (modal wasn't closed/reset)
      if (requestId !== claimRequestIdRef.current) return;

      if (claims?.length > 0) {
        const claim = claims[0];
        if (claim.policy_holder) setClientName(claim.policy_holder);
        if (claim.policy_holder_email) setClientEmail(claim.policy_holder_email);
        if (claim.policy_holder_phone) setClientPhone(formatPhone(claim.policy_holder_phone));
      }
    },
    [dispatch, projectId, formatPhone]
  );

  const resetState = useCallback(() => {
    setStep('pick');
    setSelectedTemplate(null);
    setTemplateSearch('');
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setSendMethod('email_and_sms');
    setSubmitting(false);
    setError('');
    setCreatedSubmission(null);
    // Invalidate any in-flight claim prefill requests
    claimRequestIdRef.current++;
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const onBackToTemplates = useCallback(() => {
    setStep('pick');
    setSelectedTemplate(null);
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setError('');
    setCreatedSubmission(null);
    // Invalidate any in-flight claim prefill requests
    claimRequestIdRef.current++;
  }, []);

  const onSubmit = useCallback(async () => {
    const needsEmail = sendMethod === 'email' || sendMethod === 'email_and_sms';
    const needsSms = sendMethod === 'sms' || sendMethod === 'email_and_sms';
    if (!selectedTemplate?.id || !projectId || !clientName.trim()) return;
    if (needsEmail && !clientEmail.trim()) return;
    if (needsSms && !rawPhone) return;
    setSubmitting(true);
    setError('');

    // Reuse existing submission on retry, or create a new one
    let submission = createdSubmission;
    if (!submission) {
      submission = await dispatch(
        createPdfFormSubmission({
          pdf_form_template_id: selectedTemplate.id,
          project_id: projectId,
          client_name: clientName.trim(),
          client_email: clientEmail.trim() || undefined,
          client_phone: rawPhone || undefined,
        }) as any
      );

      if (!submission) {
        setError('Failed to create submission. Please try again.');
        setSubmitting(false);
        return;
      }

      setCreatedSubmission(submission);
    }

    // Send share (email/SMS)
    const shareData: { email?: string; phone?: string } = {};
    if (needsEmail && clientEmail) shareData.email = clientEmail;
    if (needsSms && rawPhone) shareData.phone = rawPhone;

    if (shareData.email || shareData.phone) {
      const shareResult = await dispatch(sharePdfFormSubmission(submission.id, shareData) as any);
      if (!shareResult) {
        setError('Submission created but failed to send. Click the button to retry sending.');
        setSubmitting(false);
        return;
      }
    }

    setSubmitting(false);
    handleClose();
    if (onSubmissionCreated) onSubmissionCreated();
  }, [selectedTemplate, projectId, clientName, clientEmail, rawPhone, sendMethod, dispatch, handleClose, onSubmissionCreated, createdSubmission]);

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.modalOverlay} onClick={handleClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {step === 'pick' ? (
          <>
            <h2>Select a PDF Form Template</h2>
            <div className={styles.formGroup}>
              <input
                type="text"
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                placeholder="Search templates..."
              />
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {filteredTemplates.length > 0 ? (
                filteredTemplates.map((t: any) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div
                    key={t.id}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #e8e7ed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                    }}
                    onClick={() => onTemplateSelect(t)}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f4e5ff';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: 18, color: '#9a00ff' }}>&#128196;</span>
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{t.name}</span>
                  </div>
                ))
              ) : (
                <div style={{ padding: '24px 12px', textAlign: 'center', color: '#777185', fontSize: 14 }}>
                  {templateList.length === 0
                    ? 'No PDF form templates found. Create templates from the PDF Forms page first.'
                    : 'No matching templates found.'}
                </div>
              )}
            </div>
            <div className={styles.modalActions}>
              <button type="button" className={styles.btnSecondary} onClick={handleClose}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Send PDF Form to Client</h2>
            <p style={{ fontSize: 14, color: '#6c757d', margin: '0 0 16px' }}>
              {'Template: '}
              <strong>{selectedTemplate?.name}</strong>
            </p>

            <div className={styles.formGroup}>
              <label>Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="John Doe"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Client Email</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>

            <div className={styles.formGroup}>
              <label>{`Client Phone${sendMethod === 'email' ? ' (optional)' : ''}`}</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Send via</label>
              <select
                value={sendMethod}
                onChange={(e) => setSendMethod(e.target.value as 'email_and_sms' | 'email' | 'sms')}
              >
                <option value="email_and_sms">Email and SMS</option>
                <option value="email">Email only</option>
                <option value="sms">SMS only</option>
              </select>
            </div>

            {error && (
              <p style={{ color: '#dc3545', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
            )}

            <div className={styles.modalActions}>
              <button type="button" className={styles.btnSecondary} onClick={onBackToTemplates}>
                Back
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={onSubmit}
                disabled={
                  !clientName.trim() ||
                  ((sendMethod === 'email' || sendMethod === 'email_and_sms') && !clientEmail.trim()) ||
                  ((sendMethod === 'sms' || sendMethod === 'email_and_sms') && !rawPhone) ||
                  submitting
                }
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {submitting ? 'Sending...' : createdSubmission ? 'Retry Send' : 'Create & Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const SendPdfFormFromProjectModalMemo = memo(SendPdfFormFromProjectModal, areEqual);
export { SendPdfFormFromProjectModalMemo as SendPdfFormFromProjectModal };
